/**
 * Order hint utilities for computing order hints between two existing hints.
 *
 * This is based on the service implementation at:
 * https://office.visualstudio.com/Office/_git/Office?path=%2Ftodoserver%2FUtility%2FOrderHintCalculator.cs&_a=contents&version=GBmain
 *
 */ import { stringOrdinalCompare } from "./Comparators";
// Constants defining the accepted range of characters for order hints
const SEPARATOR_CHARACTER = " ";
const OPERATOR_CHARACTER = "!";
const MIN_ACCEPTED_CHAR = '"'; // 0x22
const MAX_ACCEPTED_CHAR = "~"; // 0x7e
const MID_CHAR = "P";
const MAX_ORDER_HINT_LENGTH = 50;
const FIRST_ORDER_HINT = MIN_ACCEPTED_CHAR;
const LAST_ORDER_HINT = MAX_ACCEPTED_CHAR.repeat(MAX_ORDER_HINT_LENGTH);
// Use .NET long.MaxValue
const LONG_MAX = BigInt("9223372036854775807");
// Use .NET ticks at Unix epoch (1970-01-01)
const TICKS_AT_UNIX_EPOCH = 621355968000000000n;
// Helpers for tick math (100ns units)
const TICKS_PER_MILLI = 10000n;
const TICKS_PER_HOUR = 60n * 60n * 1000n * TICKS_PER_MILLI;
const TICKS_PER_MINUTE = 60n * 1000n * TICKS_PER_MILLI;
function getTicksNow() {
    return TICKS_AT_UNIX_EPOCH + BigInt(Date.now()) * TICKS_PER_MILLI;
}
/**
 * Calculate an order hint from an expression containing order hints, the formats: "previousHint nextHint !"
 * @param input Expression to evaluate
 */ export function calculateOrderHintFromExpression(input) {
    return parseAndEvaluateExpression(input);
}
/**
 * Generate an order hint between two existing order hints.
 * @param lowOrderHint Low order hint
 * @param highOrderHint High order hint
 */ export function computeOrderHintBetween(lowOrderHint, highOrderHint) {
    const previousOrderHint = lowOrderHint || "";
    const nextOrderHint = highOrderHint || "";
    const orderHint = calculateOrderHintBetween(previousOrderHint, nextOrderHint);
    return sanitizeOrderHint(orderHint);
}
/**
 * Sanitizes an existing order hint by ensuring all characters are within the accepted range.
 * @param orderHint The order hint string to sanitize.
 */ export function sanitizeOrderHint(orderHint) {
    // traverse the string and replace invalid chars with either the min or max accepted char
    const sanitizedChars = [];
    for (const char of orderHint){
        if (char < MIN_ACCEPTED_CHAR) {
            sanitizedChars.push(MIN_ACCEPTED_CHAR);
        } else if (char > MAX_ACCEPTED_CHAR) {
            sanitizedChars.push(MAX_ACCEPTED_CHAR);
        } else {
            sanitizedChars.push(char);
        }
    }
    return sanitizedChars.join("");
}
/**
 * Calculates an order hint between two existing order hints.
 * @param previousOrderHint Previous order hint (empty string if none)
 * @param nextOrderHint Next order hint (empty string if none)
 */ function calculateOrderHintBetween(previousOrderHint, nextOrderHint) {
    if (previousOrderHint.length === 0) {
        // We'd like to be in-line with order hints auto generated by the service. The service side (not algorithm here) will auto-gen order hint values with long.MaxValue - UtcTicks.
        // This algorithm attempts to consider that as an upper bound, in order to have the items sorted correctly when order hints aren't specified.
        // If we were to use the min possible value, we'd go much below the tick calculation,
        // which means new items generated with auto-generated order hints won't be placed at the top.
        if (nextOrderHint.length !== 0) {
            let prev = (LONG_MAX - getTicksNow()).toString();
            if (stringOrdinalCompare(prev, nextOrderHint) >= 0) {
                // If the next value we have sorts before the "would be" previous value, then we need to adjust our expectations a bit.
                // It may just be a timing issue with multiple inserts done at the top, so assume up to 1 hour difference on the expected value.
                // This gives us enough room to still generate close by values.
                prev = (LONG_MAX - (getTicksNow() + TICKS_PER_HOUR)).toString();
                if (stringOrdinalCompare(prev, nextOrderHint) >= 0) {
                    // This must be some data that was created in a weird format before this enforcement. We'll need to use min values
                    prev = FIRST_ORDER_HINT;
                }
            }
            previousOrderHint = prev;
        } else {
            // Both values were empty, so we can use a time based value as the hint.
            // We'll use a value from a minute ago to provide some space between the generated value and the baseline we use to compare in the if block above.
            // This allows a gap so that the combined top values like "   !!!" do not depend on the time they hit the service as much.
            // Without this 1 minute change, the value generated would be very close to the current ticks value,
            // regardless of how many calculations are performed, Since each calculation will take an average between the the previous value and the ticks at that time.
            // With this 1 minute change, calculations cause jumps of several seconds, which should be enough to be isolated from unreliability of ticks.
            // As an example for A = "  !!" and B = " !", with simplified numbers, say execution of A happens at (max-ticks =) 1000 and
            // the second step of the calculation at 998, this will produce (avg of 1000 and 998) 999 for A. B, hits another VM, at 997, producing end result 997,
            // and B > A, which is different from the client ordering.
            // With this mechanism, the same input, and same time points, and 10 unit difference (as a simplified value) would generate
            // average of (1000 + 10 = ) 1010 998, which is 1004.
            // B, hitting the same VM, would generate (997 + 10 = ) 1007, producing A > B as the client intended.
            const orderHint = (LONG_MAX - (getTicksNow() - TICKS_PER_MINUTE)).toString();
            return orderHint + MID_CHAR;
        }
    } else {
        // Previous has a value, if next has one too, they should be in proper order
        if (nextOrderHint.length !== 0) {
            const comparison = stringOrdinalCompare(previousOrderHint, nextOrderHint);
            if (comparison === 0) {
                throw maxPrecisionException();
            } else if (comparison > 0) {
                throw invalidFormatException(`Previous order hint (${previousOrderHint}) is greater than the next order hint (${nextOrderHint}).`);
            }
        }
    }
    if (nextOrderHint.length === 0) {
        nextOrderHint = LAST_ORDER_HINT;
    }
    const maxHintLength = Math.max(previousOrderHint.length, nextOrderHint.length);
    const hintChars = new Array(maxHintLength + 1);
    // Indicates that we can ignore the rest of the next string, because the generated hint so far is
    // less than the next string. We only need to make sure it is greater than the previous string.
    let foundGap = false;
    const minCode = MIN_ACCEPTED_CHAR.charCodeAt(0);
    const maxCode = MAX_ACCEPTED_CHAR.charCodeAt(0);
    // Loop over every character to identify a gap between the previous and next values to be able to place a new order hint.
    // The gap would be the first character that is different between the previous and the next. At that point, if there is enough distance
    // between characters, we'll immediate generate a hint with a new character there.
    // If there isn't enough gap, the next value is mostly ignored afterwards to create a value.
    for(let i = 0; i < maxHintLength; i++){
        const prevCode = i < previousOrderHint.length ? previousOrderHint.charCodeAt(i) : minCode;
        const nextCode = foundGap || i >= nextOrderHint.length ? maxCode : nextOrderHint.charCodeAt(i);
        if (prevCode === nextCode) {
            // Same char, no need to calculate anything.
            hintChars[i] = String.fromCharCode(prevCode);
            continue;
        }
        // Take the middle, rounding down if gap is not found, up if it is.
        const hintCode = Math.floor((prevCode + nextCode + (foundGap ? 1 : 0)) / 2);
        hintChars[i] = String.fromCharCode(hintCode);
        if (hintCode > prevCode) {
            // The moment we don't match the previous char, we have a proper value, because either
            // we're rounding down, so mismatch indicates the value is between previous and the next.
            // we're rounding up, which only happens once we confirm we're already lesser than the next.
            return hintChars.slice(0, i + 1).join("");
        } else {
            // There definitely is space here to place an order hint, but not at this character
            // The hint characters so far are less than the next string.
            // For example for input: a b
            //  a < b but we can't generate a character that matches the a<?<b condition,
            // so we say: copying the character from a (as above), we make sure the new hint is less than the next hint,
            // so we have space to insert an order hint, but we can't do that by looking at only this character.
            foundGap = true;
        }
    }
    if (foundGap) {
        // We have run out of string space, however we know that the current hint chars sort before the next string.
        // since we run out of characters, we know that the hint chars so far are also equal to the previous string.
        // so if we add the mid char to the end of it, we will get a value that sorts correctly.
        hintChars[maxHintLength] = MID_CHAR;
        return hintChars.join("");
    }
    // We have looked at all the characters, but we didn't find any value that would sort in proper order
    // there is no gap here, this needs reorder.
    throw maxPrecisionException();
}
/**
 * Parse and evaluate an expression containing order hints, spaces (as separators) and exclamation points (as operators to calculate between the two surrounding hints).
 * @param input The input expression to parse and evaluate.
 */ function parseAndEvaluateExpression(input) {
    if (!input || input.trim().length === 0) {
        throw invalidFormatException("argument to private function is invalid");
    }
    const hints = [];
    let beginIndex = 0;
    let inputNeedsCalculation = false;
    // Initially we can see content (specific hint value) or separator between contents (= empty string content)
    let allowContent = true;
    let allowSeparator = true;
    let allowOperator = false;
    for(let i = 0; i < input.length; i++){
        const currentChar = input[i];
        if (currentChar === SEPARATOR_CHARACTER || currentChar === OPERATOR_CHARACTER) {
            inputNeedsCalculation = true;
            if (allowContent) {
                // we were reading/expecting content so we need to extract it out.
                const segment = input.substring(beginIndex, i);
                hints.push(segment);
            }
            // This character is a special one, so any possible content must exclude this.
            beginIndex = i + 1;
            if (currentChar === OPERATOR_CHARACTER) {
                if (!allowOperator || hints.length < 2) {
                    throw invalidFormatException(`The input (${input}) contains an invalid exclamation point at index ${i}.`);
                }
                const nextHintValue = hints.pop();
                const previousHintValue = hints.pop();
                const newOrderHint = calculateOrderHintBetween(previousHintValue, nextHintValue);
                hints.push(newOrderHint);
                // Operator seen, we can't see content, but we can see either a separator or an operator
                allowContent = false;
                allowSeparator = true;
                allowOperator = true;
            } else {
                if (!allowSeparator) {
                    // This isn't possible with this implementation. However, it makes the code clearer to have it here.
                    throw invalidFormatException(`The input (${input}) contains an invalid space at index ${i}.`);
                }
                // Separator seen, we can now see an operator, in addition to content or another separator (=empty content)
                allowContent = true;
                allowSeparator = true;
                allowOperator = true;
            }
        } else if (currentChar < MIN_ACCEPTED_CHAR || currentChar > MAX_ACCEPTED_CHAR) {
            throw invalidFormatException(`The input (${input}) contains an invalid character '${currentChar}' at index ${i}.`);
        } else if (!allowContent) {
            throw invalidFormatException(`The input (${input}) contains an invalid character sequence at index ${i}.`);
        }
    }
    if (!inputNeedsCalculation) {
        throw invalidFormatException(`The input (${input}) does not contain spaces or exclamation points.`);
    }
    if (hints.length !== 1 || allowContent) {
        // We need to have a single result, and we must not be in a state where we were reading content.
        throw invalidFormatException(`Input (${input}) contains more spaces than exclamation points, or does not end with an exclamation point.`);
    }
    let calculatedValue = hints.pop();
    if (calculatedValue.length > MAX_ORDER_HINT_LENGTH) {
        // Too long, I guess we run out of space?
        throw maxPrecisionException();
    }
    if (calculatedValue.length < MAX_ORDER_HINT_LENGTH) {
        // Add a char for randomization, so operations performed at the same time are likely to produce different results.
        const randomCharCode = Math.floor(Math.random() * (MAX_ACCEPTED_CHAR.charCodeAt(0) - MIN_ACCEPTED_CHAR.charCodeAt(0) + 1)) + MIN_ACCEPTED_CHAR.charCodeAt(0);
        const randomChar = String.fromCharCode(randomCharCode);
        calculatedValue += randomChar;
    }
    return calculatedValue;
}
function invalidFormatException(reason) {
    return new Error(`Specified order hint is in an invalid format: ${reason}`);
}
function maxPrecisionException() {
    return new Error("Maximum precision for order hints has been reached. The items in the collection need to be sorted again.");
}
